import { useState, useEffect } from 'react';
import { AddressForm } from '@app/components/shared/AddressForm';

interface Address {
  id: number;
  name: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ClientAddressesProps {
  customerId: number;
  cachedData?: Address[];
  onDataLoaded?: (data: Address[]) => void;
}

export function ClientAddresses({ customerId, cachedData, onDataLoaded }: ClientAddressesProps) {
  const [addresses, setAddresses] = useState<Address[]>(cachedData || []);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isLoading, setIsLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    if (cachedData) {
      setAddresses(cachedData);
      setIsLoading(false);
    } else {
      fetchAddresses();
    }
  }, [customerId, cachedData]);

  const fetchAddresses = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/customer/addresses');
      const result = await response.json();

      if (result.success) {
        setAddresses(result.data.addresses);
        onDataLoaded?.(result.data.addresses);
      } else {
        setError(result.error || 'Failed to fetch addresses');
        onDataLoaded?.([]);
      }
    } catch (error) {
      setError('Network error. Please try again.');
      onDataLoaded?.([]);
      console.error('Addresses fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (addressData: Partial<Address>) => {
    try {
      setIsSaving(true);
      setError(null);
      
      const isNew = !addressData.id;
      const url = isNew ? '/api/customer/addresses' : `/api/customer/addresses/${addressData.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addressData),
      });

      const result = await response.json();

      if (result.success) {
        await fetchAddresses();
        setEditingId(null);
        setShowAddForm(false);
        setSuccess(isNew ? 'Address added successfully!' : 'Address updated successfully!');
        setError(null);
      } else {
        setError(result.error || 'Failed to save address');
      }
    } catch (error) {
      setError('Network error. Please try again.');
      console.error('Address save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (addressId: number) => {
    if (!confirm('Are you sure you want to delete this address?')) return;

    try {
      setDeletingId(addressId);
      setError(null);
      
      const response = await fetch(`/api/customer/addresses/${addressId}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (result.success) {
        await fetchAddresses();
        setSuccess('Address deleted successfully!');
        setError(null);
      } else {
        setError(result.error || 'Failed to delete address');
      }
    } catch (error) {
      setError('Network error. Please try again.');
      console.error('Address delete error:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowAddForm(false);
    setError(null);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Addresses</h3>
            <p className="text-gray-600 text-sm mt-1">
              Manage your shipping addresses
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
          >
            Add Address
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
          {success}
        </div>
      )}

      <div className="space-y-4">
        {/* Add New Address Form */}
        {showAddForm && (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h4 className="font-medium text-gray-900 mb-4">Add New Address</h4>
            <AddressForm
              onSave={handleSave}
              onCancel={handleCancel}
              address={{
                name: '',
                street: '',
                city: '',
                state: '',
                zipCode: '',
                isDefault: addresses.length === 0,
              }}
              showDefaultOption={true}
              submitButtonText="Save Address"
              cancelButtonText="Cancel"
              isLoading={isSaving}
            />
          </div>
        )}

        {/* Address List */}
        {addresses.length === 0 && !showAddForm ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📍</div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">No addresses yet</h4>
            <p className="text-gray-600 mb-4">
              Add your first address to make checkout easier.
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 cursor-pointer"
            >
              Add Your First Address
            </button>
          </div>
        ) : (
          addresses.map((address) => (
            <div key={address.id} className="border border-gray-200 rounded-lg p-4 bg-white">
              {editingId === address.id ? (
                <AddressForm
                  address={address}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  showDefaultOption={true}
                  submitButtonText="Save Address"
                  cancelButtonText="Cancel"
                  isLoading={isSaving}
                />
              ) : (
                <AddressDisplay
                  address={address}
                  onEdit={() => setEditingId(address.id)}
                  onDelete={() => handleDelete(address.id)}
                  isDeleting={deletingId === address.id}
                />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

interface AddressDisplayProps {
  address: Address;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting?: boolean;
}

function AddressDisplay({ address, onEdit, onDelete, isDeleting = false }: AddressDisplayProps) {
  return (
    <div className="flex justify-between items-start">
      <div className="flex-1">
        <div className="flex items-center space-x-2 mb-2">
          <h4 className="font-medium text-gray-900">{address.name}</h4>
          {address.isDefault && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Default
            </span>
          )}
        </div>
        <div className="text-sm text-gray-600 space-y-1">
          <p>{address.street}</p>
          <p>{address.city}, {address.state} {address.zipCode}</p>
        </div>
      </div>

      <div className="flex space-x-2 ml-4">
        <button
          onClick={onEdit}
          disabled={isDeleting}
          className={`text-sm font-medium cursor-pointer ${
            isDeleting
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          Edit
        </button>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className={`text-sm font-medium cursor-pointer ${
            isDeleting
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-red-600 hover:text-red-700'
          }`}
        >
          {isDeleting ? (
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 border-2 border-gray-400/30 border-t-gray-400 rounded-full animate-spin"></span>
              Deleting...
            </span>
          ) : (
            'Delete'
          )}
        </button>
      </div>
    </div>
  );
}